import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	AGENCY_LIST,
	APPROACH_SOURCE_LIST,
	PREFIX_LIST,
} from '@/common/constants/common';
import { useGetDicListQuery, useGetDicTreeListMutation } from '@/api/common';
import { SystemDictTreeDTO } from '@/api/DTO/common.interface';
import AppDropDownTreeMenu from '@/common/components/form-inputs/AppDropDownTreeMenu';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import { eightDigitPhone } from '@/utils/validationHelper';
import {
	getProfileOfReferralId,
	isProfileOfReferralOthers,
} from '@/utils/referralHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditReferrerInformationForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const [getDicTreeList] = useGetDicTreeListMutation();
		const [referralAgencyList, setReferralAgencyList] = useState<
			SystemDictTreeDTO[]
		>([]);

		const { data: profileReferralsDicList } = useGetDicListQuery(
			'profile_of_referrals',
			{ refetchOnMountOrArgChange: true }
		);

		const isOthers = (id: string) =>
			isProfileOfReferralOthers(profileReferralsDicList?.data, id);
		const isOthers2 = (id: string) => {
			const obj = referralAgencyList
				.flatMap((item: any) => item.childrenList ?? [])
				.find((child: any) => child.id == id);
			return obj && obj.labelEn == 'Others';
		};

		const defaultValues: DefaultValues<any> = {
			approachSource: null,
		};

		useEffect(() => {
			if (details && profileReferralsDicList) {
				methods.reset(details);

				if (details.profileOfReferral)
					handleSetReferralAgencyList(details.profileOfReferral);
			}
		}, [details, profileReferralsDicList]);

		const formSchema = Yup.object().shape({
			approachSource: Yup.string().required(t('errorMessage.pleaseSelect')),
			referrerContactNo: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			referrerFaxNo: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			profileOfReferral: Yup.string().required(t('errorMessage.pleaseSelect')),
			profileOfReferralSpecify: Yup.string().when('profileOfReferral', {
				is: (id: string) => !!id && isOthers(id),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			referralAgencySpecify: Yup.string().when('referralAgency', {
				is: (id: string) => !!id && isOthers2(id),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchReferralAgency = methods.watch('referralAgency');
		const watchProfileOfReferral = methods.watch('profileOfReferral');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const handleSetReferralAgencyList = async (e: string) => {
			setReferralAgencyList([]);
			if (!e) return;
			const response = await getDicTreeList(e);
			if ('data' in response)
				setReferralAgencyList(
					response.data?.data.map((item) => {
						const childrenList = (item.childrenList || []).map((child) => {
							return {
								...child,
								name: child.labelEn || '',
							};
						});
						return {
							...item,
							childrenList: childrenList,
							name: item.labelEn || '',
						};
					}) || []
				);
		};

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.referrerInformation')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.approachSource') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='approachSource'
									row
									options={APPROACH_SOURCE_LIST}
									handleChange={(e: any) => {
										const profileId = getProfileOfReferralId(
											profileReferralsDicList?.data,
											e.target.value
										);
										methods.setValue('profileOfReferral', profileId);
										methods.setValue('profileOfReferralSpecify', '');
										methods.setValue('referralAgency', '');
										methods.setValue('referralAgencySpecify', '');
										methods.clearErrors([
											'profileOfReferralSpecify',
											'referralAgencySpecify',
										]);
										handleSetReferralAgencyList(profileId);
										methods.trigger('approachSource');
										methods.trigger('profileOfReferral');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppAutoCompleteMenu
									control={methods.control}
									name='profileOfReferral'
									label={t('preAdmission.profileOfReferrals') + '*'}
									options={(profileReferralsDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
									handleChange={(e: any) => {
										methods.setValue('referralAgency', '');
										methods.setValue('profileOfReferralSpecify', '');
										methods.clearErrors('profileOfReferralSpecify');
										methods.setValue('referralAgencySpecify', '');
										methods.clearErrors('referralAgencySpecify');
										handleSetReferralAgencyList(e);
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!isOthers(watchProfileOfReferral)}
									control={methods.control}
									name='profileOfReferralSpecify'
									label={
										t('preAdmission.profileOfReferrals(specify)') +
										(isOthers(watchProfileOfReferral) ? '*' : '')
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppAutoCompleteMenu
									disabled={!watchProfileOfReferral}
									control={methods.control}
									name='referralAgency'
									label={t('preAdmission.referralAgency')}
									options={referralAgencyList}
									handleChange={(e: any) => {
										methods.setValue('referralAgencySpecify', '');
										methods.clearErrors('referralAgencySpecify');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!isOthers2(watchReferralAgency)}
									control={methods.control}
									name='referralAgencySpecify'
									label={
										t('preAdmission.referralAgency(specify)') +
										(isOthers2(watchReferralAgency) ? '*' : '')
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='prefixOfReferrer'
									label={t('preAdmission.prefix')}
									options={PREFIX_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameOfReferrer'
									label={t('preAdmission.nameOfReferrer')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='positionOfReferrer'
									label={t('preAdmission.positionOfReferrer')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='referrerContactNo'
									label={t('preAdmission.referrerContactNo')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='referrerFaxNo'
									label={t('preAdmission.referrerFaxNo')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditReferrerInformationForm.displayName = 'EditReferrerInformationForm';

export default EditReferrerInformationForm;
